import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ArticleCreate, ArticleStatus, Article } from '../types';
import { apiService } from '../services/api';
import { MarkdownEditor } from './admin/MarkdownEditor';

interface ArticleFormProps {
  onSubmit: (data: ArticleCreate, imageFile?: File) => Promise<Article | void>;
  onCancel: () => void;
  loading: boolean;
  error: string | null;
  initialData?: Article;
  onFileUpload?: (articleId: number, files: File[]) => Promise<void>;
  onFileDelete?: (articleId: number, fileIndex: number) => Promise<void>;
}

export const ArticleForm: React.FC<ArticleFormProps> = ({ 
  onSubmit, 
  onCancel, 
  loading, 
  error,
  initialData,
  onFileUpload,
  onFileDelete,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ArticleCreate>({
    title: initialData?.title || '',
    content: initialData?.content || '',
    status: (initialData?.status as ArticleStatus) || ArticleStatus.DRAFT,
    published_at: initialData?.published_at ? new Date(initialData.published_at).toISOString().split('T')[0] : undefined,
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [audioFiles, setAudioFiles] = useState<File[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [deletingAudioIndex, setDeletingAudioIndex] = useState<number | null>(null);
  const isEditing = !!initialData?.id;

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        content: initialData.content || '',
        status: (initialData.status as ArticleStatus) || ArticleStatus.DRAFT,
        published_at: initialData.published_at ? new Date(initialData.published_at).toISOString().split('T')[0] : undefined,
      });
      setImageFile(null);
      setAudioFiles([]);
      setFiles([]);
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.content) return;
    
    setUploadingFiles(true);
    try {
      // Convert date string to ISO datetime string if provided
      const submitData = { ...formData };
      if (submitData.published_at) {
        // Convert "YYYY-MM-DD" to "YYYY-MM-DDTHH:MM:SS" format
        submitData.published_at = `${submitData.published_at}T00:00:00`;
      }
      
      // Submit article data
      const result = await onSubmit(submitData, imageFile || undefined);
      const article = (result as Article) || initialData;
      const articleId = article?.id || initialData?.id;
      
      if (!articleId) {
        console.error('No article ID available for file uploads', {
          result,
          article,
          initialData,
          hasResult: !!result,
          hasArticle: !!article,
          hasInitialData: !!initialData
        });
        // Don't return early - still try to upload if we have initialData with ID
        if (!initialData?.id) {
          return;
        }
      }
      
      // Use the articleId we found (either from result or initialData)
      const finalArticleId = articleId || initialData?.id;

      // Upload audio files if provided
      if (audioFiles.length > 0 && finalArticleId) {
        try {
          for (const audioFile of audioFiles) {
            await apiService.uploadArticleAudio(finalArticleId, audioFile);
          }
          setAudioFiles([]);
        } catch (err) {
          console.error('Error uploading audio:', err);
        }
      }

      // Upload files if provided
      if (files.length > 0 && finalArticleId) {
        try {
          if (onFileUpload) {
            await onFileUpload(finalArticleId, files);
          } else {
            // Fallback: upload files directly
            for (const file of files) {
              await apiService.uploadArticleFile(finalArticleId, file);
            }
          }
          setFiles([]);
        } catch (err) {
          console.error('Error uploading files:', err);
        }
      }
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleAudioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const fileArray = Array.from(e.target.files);
      setAudioFiles((prev) => [...prev, ...fileArray]);
    }
  };

  const handleRemoveAudioFile = (index: number) => {
    setAudioFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingAudio = async (audioIndex: number) => {
    if (initialData?.id) {
      try {
        setDeletingAudioIndex(audioIndex);
        await apiService.deleteArticleAudio(initialData.id, audioIndex);
        // Don't reload, close the modal and let parent refresh
        onCancel();
      } catch (err) {
        console.error('Error deleting audio:', err);
        alert('Failed to delete audio file');
      } finally {
        setDeletingAudioIndex(null);
      }
    }
  };

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const fileArray = Array.from(e.target.files);
      setFiles((prev) => [...prev, ...fileArray]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingFile = async (fileIndex: number) => {
    if (initialData?.id && onFileDelete) {
      try {
        await onFileDelete(initialData.id, fileIndex);
      } catch (err) {
        console.error('Error deleting file:', err);
      }
    }
  };

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
      <div className='flex justify-between items-center mb-6'>
        <h2 className='text-lg font-semibold text-gray-900'>
          {isEditing ? t('article.edit_article') : t('article.create_article')}
        </h2>
      </div>

      {error && (
        <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-md'>
          <p className='text-red-600 text-sm'>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className='space-y-4'>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Titre *
          </label>
          <input
            type='text'
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            placeholder={t('article.article_title')}
            required
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Contenu * (Markdown)
          </label>
          <MarkdownEditor
            value={formData.content}
            onChange={(content) => setFormData({ ...formData, content })}
            placeholder={t('article.article_content')}
          />
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              Statut
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as ArticleStatus })}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              <option value={ArticleStatus.DRAFT}>{t('article.draft')}</option>
              <option value={ArticleStatus.PUBLISHED}>{t('article.published')}</option>
            </select>
          </div>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              {t('article.publication_date')}
            </label>
            <input
              type='date'
              value={formData.published_at || ''}
              onChange={(e) => {
                const dateValue = e.target.value || undefined;
                setFormData({ ...formData, published_at: dateValue });
              }}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              {t('article.image_optional')}
            </label>
            {initialData?.image_path && (
              <div className='mb-3'>
                <p className='text-sm text-gray-600 mb-2'>{t('article.current_image')}</p>
                <img
                  src={apiService.getArticleImageUrl(initialData.id)}
                  alt={initialData.title}
                  className='w-full max-w-md h-auto rounded-lg border border-gray-200'
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}
            <input
              type='file'
              accept='image/jpeg,image/png,image/gif,image/webp'
              onChange={handleImageChange}
              className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            />
            {imageFile && (
              <p className='mt-1 text-sm text-gray-500'>
                {t('common.new_file_selected')} {imageFile.name}
              </p>
            )}
          </div>
        </div>

        {/* Audio Files Section */}
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Fichiers Audio (Optionnels)
          </label>
          
          {/* Existing audio files */}
          {isEditing && initialData?.audio_files && initialData.audio_files.length > 0 && (
            <div className='mb-3'>
              <p className='text-sm text-gray-600 mb-2'>Fichiers audio existants:</p>
              <div className='space-y-2'>
                {initialData.audio_files.map((audioFile, index) => (
                  <div key={index} className='flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200'>
                    <div className='flex-1 mr-3'>
                      <p className='text-sm font-medium text-gray-800 mb-1'>{audioFile.caption}</p>
                      <audio
                        controls
                        src={apiService.getArticleAudioUrl(initialData.id, index)}
                        className='w-full'
                        style={{ pointerEvents: 'auto' }}
                      >
                        {t('common.browser_no_audio')}
                      </audio>
                    </div>
                    <button
                      type='button'
                      onClick={() => handleDeleteExistingAudio(index)}
                      disabled={deletingAudioIndex === index}
                      className='px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors disabled:opacity-50 inline-flex items-center gap-1'
                    >
                      {deletingAudioIndex === index ? (
                        'Suppression...'
                      ) : (
                        <>
                          <X className='w-4 h-4' /> Supprimer
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New audio files to upload */}
          <input
            type='file'
            multiple
            accept='audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/webm,audio/aac,audio/flac'
            onChange={handleAudioChange}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
          />
          <p className='mt-1 text-xs text-gray-500'>
            Vous pouvez sélectionner plusieurs fichiers audio. La légende sera générée automatiquement à partir du nom du fichier.
          </p>
          
          {audioFiles.length > 0 && (
            <div className='mt-2 space-y-1'>
              {audioFiles.map((file, index) => (
                <div key={index} className='flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200'>
                  <span className='text-sm text-gray-700'>{file.name}</span>
                  <button
                    type='button'
                    onClick={() => handleRemoveAudioFile(index)}
                    className='text-red-600 hover:text-red-800 text-sm'
                  >
                    <X className='w-4 h-4' />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            {t('article.attached_files')} {isEditing ? `(${t('common.optional')})` : ''}
          </label>
          <input
            type='file'
            multiple
            accept='.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip'
            onChange={handleFilesChange}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
          />
          <p className='mt-1 text-xs text-gray-500'>
            {isEditing 
              ? t('article.select_files_add')
              : t('article.select_files_join')}
          </p>
          {files.length > 0 && (
            <div className='mt-2 space-y-1'>
              {files.map((file, index) => (
                <div key={index} className='flex items-center justify-between p-2 bg-gray-50 rounded'>
                  <span className='text-sm text-gray-700'>{file.name}</span>
                  <button
                    type='button'
                    onClick={() => handleRemoveFile(index)}
                    className='text-red-600 hover:text-red-800 text-sm'
                  >
                    <X className='w-4 h-4' />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {isEditing && initialData?.files && initialData.files.length > 0 && (
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              {t('article.existing_files')}
            </label>
            <div className='space-y-1'>
              {initialData.files.map((file, index) => {
                // Extract filename and clean it up (remove UUID suffix if present)
                  let fileName = file.split('/').pop() || `${t('article.file')} ${index + 1}`;
                // Remove UUID suffix pattern: _xxxxxxxx.ext -> .ext, then reconstruct
                const match = fileName.match(/^(.+)_[a-f0-9]{8}(\.[^.]+)$/);
                if (match) {
                  fileName = match[1] + match[2]; // Original name + extension
                }
                return (
                  <div key={index} className='flex items-center justify-between p-2 bg-gray-50 rounded'>
                    <a
                      href={apiService.getArticleFileUrl(initialData.id, index)}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-sm text-blue-600 hover:text-blue-800'
                    >
                      {fileName}
                    </a>
                    {onFileDelete && (
                      <button
                        type='button'
                        onClick={() => handleDeleteExistingFile(index)}
                        className='text-red-600 hover:text-red-800 text-sm'
                      >
                        {t('common.delete')}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className='flex gap-3 pt-4'>
          <button
            type='submit'
            disabled={loading || uploadingFiles || !formData.title || !formData.content}
            className='flex-1 bg-church-brown text-white py-3 px-6 rounded-lg hover:bg-church-dark transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-md hover:shadow-lg disabled:shadow-none'
          >
            {loading || uploadingFiles
              ? isEditing
                ? t('article.updating')
                : t('article.creating')
              : isEditing
              ? t('article.update')
              : t('article.create')}
          </button>
          <button
            type='button'
            onClick={onCancel}
            className='px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium'
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};